/**
 * Paiements à valider : carte (référence FAM) + virements (référence
 * bancaire). Tabs en attente / payés.
 */
import type { Payment } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  confirmCardPayment,
  listPaymentsByStatus,
  validateBankTransfer,
} from '@/features/admin/admin-api';
import { extractErrorMessage } from '@/features/auth/error-message';

export function AdminPaymentsPage() {
  const qc = useQueryClient();
  const pending = useQuery({
    queryKey: ['admin', 'payments', 'en_attente'],
    queryFn: () => listPaymentsByStatus('en_attente'),
  });
  const paid = useQuery({
    queryKey: ['admin', 'payments', 'paye'],
    queryFn: () => listPaymentsByStatus('paye'),
  });

  const confirmMut = useMutation({
    mutationFn: ({
      paymentId,
      famReference,
    }: {
      paymentId: string;
      famReference: string;
    }) => confirmCardPayment({ paymentId, famReference }),
    onSuccess: () => {
      toast.success('Paiement confirmé');
      qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });
  const transferMut = useMutation({
    mutationFn: ({
      paymentId,
      bankReference,
    }: {
      paymentId: string;
      bankReference: string;
    }) => validateBankTransfer({ paymentId, bankReference }),
    onSuccess: () => {
      toast.success('Virement validé');
      qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <DashboardLayout variant="admin" title="Paiements">
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            En attente ({pending.data?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="paid">
            Payés ({paid.data?.length ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Paiements en attente</CardTitle>
              <CardDescription>
                Confirmez les paiements carte (référence FAM) ou validez les
                virements bancaires (référence virement).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {pending.data?.length ? (
                pending.data.map((p) => (
                  <PendingPaymentRow
                    key={p.id}
                    payment={p}
                    onConfirmCard={(ref) =>
                      confirmMut.mutate({ paymentId: p.id, famReference: ref })
                    }
                    onValidateTransfer={(ref) =>
                      transferMut.mutate({ paymentId: p.id, bankReference: ref })
                    }
                  />
                ))
              ) : (
                <p className="text-sm text-gray-500">Rien à valider.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="paid">
          <Card>
            <CardHeader>
              <CardTitle>Paiements encaissés</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Méthode</TableHead>
                    <TableHead>Montant TTC</TableHead>
                    <TableHead>Référence</TableHead>
                    <TableHead className="text-right">Payé le</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paid.data?.length ? (
                    paid.data.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              p.method === 'carte'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-emerald-100 text-emerald-800'
                            }
                          >
                            {p.method}
                          </Badge>
                        </TableCell>
                        <TableCell>{p.amountTtc.toFixed(2)} €</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {p.famReference ?? p.bankReference ?? '—'}
                        </TableCell>
                        <TableCell className="text-right text-sm text-gray-500">
                          {p.paidAt
                            ? new Date(p.paidAt).toLocaleDateString('fr-FR')
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                        Aucun paiement.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function PendingPaymentRow({
  payment,
  onConfirmCard,
  onValidateTransfer,
}: {
  payment: Payment;
  onConfirmCard: (ref: string) => void;
  onValidateTransfer: (ref: string) => void;
}) {
  const [ref, setRef] = useState('');
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-white p-3 text-sm">
      <div>
        <p className="font-medium uppercase">
          {payment.method} · {payment.amountTtc.toFixed(2)} €
        </p>
        <p className="text-xs text-gray-500">Projet {payment.projectId}</p>
      </div>
      <div className="flex items-center gap-2">
        <Input
          placeholder={payment.method === 'carte' ? 'famReference' : 'bankReference'}
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          className="h-9 w-48"
        />
        {payment.method === 'carte' ? (
          <Button size="sm" onClick={() => onConfirmCard(ref)}>
            Confirmer
          </Button>
        ) : (
          <Button size="sm" onClick={() => onValidateTransfer(ref)}>
            Valider
          </Button>
        )}
      </div>
    </div>
  );
}
